/** Human-readable Telegram delivery errors for the admin UI. */

import { ApiError } from '@/api/http';

const TELEGRAM_ERROR_MESSAGES: Record<string, string> = {
  telegram_credentials_missing:
    'Telegram is not configured. Add a bot token or Telethon credentials in Configuration.',
  telegram_bot_chat_not_found:
    'Bot API failed because the recipient has not started the bot. Open the bot and send /start, then retry.',
  telegram_bot_start_required:
    'Bot cannot resolve this username. Ask the recipient to open the bot and send /start, then resolve their numeric chat ID.',
  telegram_bot_blocked_by_user: 'The recipient has blocked the bot.',
  telegram_username_not_resolved:
    'Username could not be resolved to a numeric chat ID. Check Telethon session authorization and the username.',
  telegram_username_not_found:
    'Telethon could not find that Telegram username. Verify the handle is correct and visible to your signed-in account.',
  telegram_telethon_peer_not_found:
    'Telethon could not resolve this recipient in your session. Ensure they appear in Known Chats or your Telegram contacts/dialogs.',
  telegram_invalid_chat_id:
    'Recipient "me" is not valid for bot mode. Use a numeric Telegram chat ID or Telethon session.',
  telegram_session_not_authorized:
    'Telethon session is missing or not authorized. Run auth to sign in.',
  telegram_session_missing: 'Telethon session file is missing. Run auth to create it.',
  telegram_telethon_send_failed:
    'Telethon could not deliver this message. Check session authorization and recipient visibility.',
  telethon_entity_not_resolved:
    'Telethon could not resolve this recipient in your session. Re-select them from Known Chats or ensure they appear in your dialogs or contacts.',
  telethon_peer_id_invalid: 'Telethon rejected the peer ID for this recipient.',
  telethon_user_id_invalid: 'Telethon rejected the user ID for this recipient.',
  telethon_user_privacy_restricted:
    "The recipient's privacy settings block messages from your account.",
  telethon_chat_write_forbidden: 'Your account cannot send messages to this chat.',
  telethon_flood_wait: 'Telegram rate-limited this send. Wait and retry.',
  telethon_session_unauthorized: 'Telethon session is not authorized. Run auth to sign in.',
  telethon_rpc_error: 'Telethon returned an RPC error while sending.',
  telethon_session_busy: 'Telegram session is busy. Try again shortly.',
  telethon_session_locked: 'Telegram session is busy; retry in a moment.',
  telegram_send_failed: 'Telegram rejected the message. Check server logs for details.',
};

export type TelegramErrorContext = {
  finalSendMode?: string | null;
  finalMode?: string | null;
  failureStep?: string | null;
  telethonResolutionAttempted?: boolean | null;
  deliveryProvider?: string | null;
  attemptedModes?: string[] | null;
  modeTried?: string[] | null;
  telethonExceptionType?: string | null;
  telethonExceptionMessage?: string | null;
  entitySource?: string | null;
  sessionLabel?: string | null;
  botAttempted?: boolean | null;
  accountName?: string | null;
  peerType?: string | null;
  accessHashCached?: boolean | null;
  resolvedUsername?: string | null;
  durationMs?: number | null;
  result?: string | null;
  dialogCacheUsed?: boolean | null;
  contactsUsed?: boolean | null;
};

function formatProviderLabel(provider: string | null | undefined): string {
  const normalized = (provider || 'auto').toLowerCase();
  if (normalized === 'telethon') return 'Telethon';
  if (normalized === 'bot') return 'Bot API';
  return 'Auto';
}

function formatEntitySourceLabel(entitySource: string | null | undefined): string {
  if (!entitySource) return 'missing';
  if (entitySource === 'cached') return 'cached known chat';
  if (entitySource.endsWith('_refresh')) return 'resolved';
  return entitySource.replace(/_/g, ' ');
}

export function formatTelegramError(
  error: string | null | undefined,
  errorCode?: string | null,
  context?: TelegramErrorContext,
): string {
  const finalMode = context?.finalSendMode ?? context?.finalMode;
  const botOnlyCodes = new Set([
    'telegram_bot_chat_not_found',
    'telegram_bot_start_required',
  ]);
  if (errorCode && botOnlyCodes.has(errorCode) && finalMode && finalMode !== 'bot') {
    if (context?.failureStep === 'username_not_found') {
      return TELEGRAM_ERROR_MESSAGES.telegram_username_not_found;
    }
    if (context?.telethonResolutionAttempted) {
      return TELEGRAM_ERROR_MESSAGES.telegram_username_not_found;
    }
  }
  if (errorCode && TELEGRAM_ERROR_MESSAGES[errorCode]) {
    if (
      errorCode === 'telegram_bot_chat_not_found' &&
      (context?.deliveryProvider === 'telethon' ||
        (context?.attemptedModes?.length === 1 && context.attemptedModes[0] === 'telethon'))
    ) {
      return TELEGRAM_ERROR_MESSAGES.telegram_telethon_peer_not_found;
    }
    if (
      errorCode === 'telegram_bot_chat_not_found' &&
      error &&
      error.includes('Telethon was not attempted')
    ) {
      return error.includes(': ') ? error.split(': ', 2)[1] : error;
    }
    const base = TELEGRAM_ERROR_MESSAGES[errorCode];
    if (context?.telethonExceptionType && context?.telethonExceptionMessage) {
      return `${base} (${context.telethonExceptionType}: ${context.telethonExceptionMessage})`;
    }
    return base;
  }
  if (!error) return 'Send failed';
  if (error.includes(': ')) {
    const [code, msg] = error.split(': ', 2);
    if (TELEGRAM_ERROR_MESSAGES[code]) return TELEGRAM_ERROR_MESSAGES[code];
    return msg;
  }
  return error;
}

export function formatTelegramDiagnostics(result: {
  telegramSenderMode?: string | null;
  telethonConfigured?: boolean | null;
  telethonSessionExists?: boolean | null;
  telethonResolutionAttempted?: boolean | null;
  botResolutionAttempted?: boolean | null;
  normalizedUsername?: string | null;
  resolvedChatId?: string | null;
  resolutionMethod?: string | null;
  finalMode?: string | null;
  finalSendMode?: string | null;
  failureStep?: string | null;
  deliveryProvider?: string | null;
  attemptedModes?: string[] | null;
  modeTried?: string[] | null;
  telethonExceptionType?: string | null;
  telethonExceptionMessage?: string | null;
  entitySource?: string | null;
  sessionLabel?: string | null;
  botAttempted?: boolean | null;
}): string | null {
  const parts: string[] = [];
  if (result.deliveryProvider) {
    parts.push(`Delivery provider: ${formatProviderLabel(result.deliveryProvider)}`);
  }
  const attempted = result.attemptedModes ?? result.modeTried;
  if (attempted?.length) {
    parts.push(
      `Attempted: ${attempted.map((m) => (m === 'telethon' ? 'Telethon' : m === 'bot' ? 'Bot' : m)).join(' → ')}`,
    );
  }
  if (result.sessionLabel) {
    parts.push(`Session: ${result.sessionLabel}`);
  }
  if (result.entitySource) {
    parts.push(`Entity source: ${formatEntitySourceLabel(result.entitySource)}`);
  }
  if (result.telethonExceptionType && result.telethonExceptionMessage) {
    parts.push(`Error: ${result.telethonExceptionType}: ${result.telethonExceptionMessage}`);
  }
  if (result.botAttempted === false || (attempted?.length && !attempted.includes('bot'))) {
    parts.push('Bot attempted: No');
  } else if (result.botAttempted) {
    parts.push('Bot attempted: Yes');
  }
  if (result.resolvedChatId) {
    parts.push(`resolved ${result.resolvedChatId}`);
  }
  const mode = result.finalSendMode ?? result.finalMode;
  if (!attempted?.length && mode) {
    parts.push(`via ${mode === 'telethon' ? 'Telethon' : mode === 'bot' ? 'Bot' : mode}`);
  } else if (!attempted?.length && result.resolutionMethod) {
    parts.push(`method ${result.resolutionMethod}`);
  }
  if (result.failureStep) {
    parts.push(`step ${result.failureStep}`);
  }
  if (
    result.telethonResolutionAttempted === false &&
    result.telegramSenderMode === 'auto' &&
    !attempted?.includes('telethon')
  ) {
    parts.push('Telethon not attempted');
  }
  return parts.length ? parts.join(' · ') : null;
}

/** Structured multi-line diagnostics for admin detail panels (never includes secrets). */
export function formatTelegramDiagnosticsDetailed(result: TelegramErrorContext & {
  normalizedUsername?: string | null;
  resolvedChatId?: string | null;
  resolutionMethod?: string | null;
  telegramSenderMode?: string | null;
}): string[] {
  const lines: string[] = [];
  if (result.sessionLabel) lines.push(`Telethon session: ${result.sessionLabel}`);
  if (result.accountName) lines.push(`Account name: ${result.accountName}`);
  if (result.entitySource) lines.push(`Entity source: ${formatEntitySourceLabel(result.entitySource)}`);
  if (result.dialogCacheUsed != null) {
    lines.push(`Dialog cache: ${result.dialogCacheUsed ? 'used' : 'not used'}`);
  }
  if (result.contactsUsed != null) {
    lines.push(`Contacts: ${result.contactsUsed ? 'used' : 'not used'}`);
  }
  const username = result.resolvedUsername ?? result.normalizedUsername;
  if (username) lines.push(`Resolved username: @${username.replace(/^@+/, '')}`);
  if (result.resolvedChatId) lines.push(`Resolved chat ID: ${result.resolvedChatId}`);
  if (result.peerType) lines.push(`Peer type: ${result.peerType}`);
  if (result.accessHashCached != null) {
    lines.push(`Access hash cached: ${result.accessHashCached ? 'yes' : 'no'}`);
  }
  if (result.deliveryProvider) {
    lines.push(`Delivery provider: ${formatProviderLabel(result.deliveryProvider)}`);
  }
  const attempted = result.attemptedModes ?? result.modeTried;
  if (attempted?.length) {
    lines.push(
      `Attempted modes: ${attempted.map((m) => (m === 'telethon' ? 'Telethon' : m === 'bot' ? 'Bot' : m)).join(' → ')}`,
    );
  }
  if (result.durationMs != null) lines.push(`Duration: ${result.durationMs} ms`);
  if (result.result) lines.push(`Result: ${result.result}`);
  if (result.telethonExceptionType && result.telethonExceptionMessage) {
    lines.push(`Exception: ${result.telethonExceptionType}: ${result.telethonExceptionMessage}`);
  } else if (result.failureStep) {
    lines.push(`Failure step: ${result.failureStep}`);
  }
  return lines;
}

export function isNumericChatId(chatId: string): boolean {
  return /^-?\d+$/.test(chatId.trim());
}

export function isPlainUsername(chatId: string): boolean {
  const c = chatId.trim().replace(/^@/, '');
  return Boolean(c && c !== 'me' && !isNumericChatId(c));
}

/** True when value is empty or a valid Telegram username (with or without @). */
export function isTelegramUsernameLike(value: string): boolean {
  const c = value.trim().replace(/^@+/, '');
  if (!c || c === 'me' || isNumericChatId(c)) return false;
  return /^[a-zA-Z][a-zA-Z0-9_]{4,31}$/.test(c);
}

export type TelegramSenderMode = 'auto' | 'bot' | 'telethon';

export function meRecipientWarning(senderMode: TelegramSenderMode): string {
  if (senderMode === 'bot') {
    return '"me" is invalid for bot mode.';
  }
  if (senderMode === 'telethon') {
    return '"me" is valid with Telethon.';
  }
  return '"me" will use Telethon if your session is authorized.';
}

export function isTelethonSessionBusyError(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  const detail = err.detail;
  if (detail && typeof detail === 'object' && 'code' in detail) {
    return (detail as { code?: string }).code === 'telethon_session_busy';
  }
  return err.status === 503 && err.message.toLowerCase().includes('busy');
}

export const TELETHON_SESSION_BUSY_CACHED_MESSAGE =
  'Telegram session is busy. Showing cached chats.';

export function extractBusyKnownChats<T>(detail: unknown): T[] | null {
  if (!detail || typeof detail !== 'object' || !('updates' in detail)) {
    return null;
  }
  const updates = (detail as { updates?: unknown }).updates;
  if (!Array.isArray(updates) || updates.length === 0) {
    return null;
  }
  return updates as T[];
}
